import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../atoms";
import { FormField } from "../FormField";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { investorFounderAPI } from "@/services/api/investor.service";
import type { BusinessPlanContext, FundingStage } from "@/types/startup.types";

const FUNDING_STAGES: Array<{ value: FundingStage; label: string }> = [
  { value: "bootstrapped", label: "Bootstrapped" },
  { value: "pre_seed", label: "Pre-seed" },
  { value: "seed", label: "Seed" },
  { value: "series_a", label: "Series A" },
  { value: "bridge", label: "Bridge" },
  { value: "other", label: "Other" },
];

interface BusinessPlanContextSettingsProps {
  startupId: string;
  onSaved?: () => void;
}

export const BusinessPlanContextSettings: React.FC<BusinessPlanContextSettingsProps> = ({
  startupId,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["businessPlanContext", startupId],
    queryFn: () => investorFounderAPI.getBusinessPlanContext(startupId),
    enabled: Boolean(startupId),
  });

  const defaultValues = useMemo(
    () => ({
      problemStatement: data?.problemStatement ?? "",
      solutionDescription: data?.solutionDescription ?? "",
      targetCustomer: data?.targetCustomer ?? "",
      valueProposition: data?.valueProposition ?? "",
      marketCategory: data?.marketCategory ?? "",
      geographicFocus: data?.geographicFocus ?? "",
      competitiveLandscape: data?.competitiveLandscape ?? "",
      differentiation: data?.differentiation ?? "",
      tractionHighlights: data?.tractionHighlights ?? "",
      foundingTeam: data?.foundingTeam ?? "",
      milestonesAndRoadmap: data?.milestonesAndRoadmap ?? "",
      fundingStage: data?.fundingStage ?? "",
      fundingStageLabel: data?.fundingStageLabel ?? "",
      previousFundingRaised: data?.previousFundingRaised ?? "",
      previousInvestors: data?.previousInvestors ?? "",
      useOfFunds: data?.useOfFunds ?? "",
      fundingTimeline: data?.fundingTimeline ?? "",
      generationGuidance: data?.generationGuidance ?? "",
      investorPitch: data?.investorPitch ?? "",
    }),
    [data],
  );

  const { register, handleSubmit, reset, formState: { isDirty } } = useForm({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const saveMutation = useMutation({
    mutationFn: (payload: BusinessPlanContext) =>
      investorFounderAPI.updateBusinessPlanContext(startupId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["businessPlanContext", startupId] });
      onSaved?.();
    },
  });

  return (
    <SettingsSectionCard
      title="Business plan inputs"
      description="Narrative context for AI business plan generation. Financial numbers come from your burn chart automatically."
    >
      {isLoading ? (
        <div className="text-sm text-white/55">Loading...</div>
      ) : (
        <form
          onSubmit={handleSubmit(async (values) => {
            await saveMutation.mutateAsync({
              problemStatement: values.problemStatement || undefined,
              solutionDescription: values.solutionDescription || undefined,
              targetCustomer: values.targetCustomer || undefined,
              valueProposition: values.valueProposition || undefined,
              marketCategory: values.marketCategory || undefined,
              geographicFocus: values.geographicFocus || undefined,
              competitiveLandscape: values.competitiveLandscape || undefined,
              differentiation: values.differentiation || undefined,
              tractionHighlights: values.tractionHighlights || undefined,
              foundingTeam: values.foundingTeam || undefined,
              milestonesAndRoadmap: values.milestonesAndRoadmap || undefined,
              fundingStage: (values.fundingStage as FundingStage) || undefined,
              fundingStageLabel: values.fundingStageLabel || undefined,
              previousFundingRaised: values.previousFundingRaised
                ? Number(values.previousFundingRaised)
                : undefined,
              previousInvestors: values.previousInvestors || undefined,
              useOfFunds: values.useOfFunds || undefined,
              fundingTimeline: values.fundingTimeline || undefined,
              generationGuidance: values.generationGuidance || undefined,
              investorPitch: values.investorPitch || undefined,
            });
            reset(values);
          })}
          className="space-y-4"
        >
          <FormField label="Problem statement">
            <textarea {...register("problemStatement")} className="sh-select min-h-[80px]" />
          </FormField>
          <FormField label="Solution / product">
            <textarea {...register("solutionDescription")} className="sh-select min-h-[80px]" />
          </FormField>
          <FormField label="Target customer (ICP)">
            <textarea {...register("targetCustomer")} className="sh-select min-h-[64px]" />
          </FormField>
          <FormField label="Value proposition">
            <textarea {...register("valueProposition")} className="sh-select min-h-[64px]" />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Market category">
              <input {...register("marketCategory")} className="sh-select" />
            </FormField>
            <FormField label="Geographic focus">
              <input {...register("geographicFocus")} className="sh-select" />
            </FormField>
          </div>
          <FormField label="Competitive landscape">
            <textarea {...register("competitiveLandscape")} className="sh-select min-h-[72px]" />
          </FormField>
          <FormField label="Differentiation / moat">
            <textarea {...register("differentiation")} className="sh-select min-h-[72px]" />
          </FormField>
          <FormField label="Traction highlights (qualitative)">
            <textarea {...register("tractionHighlights")} className="sh-select min-h-[72px]" />
          </FormField>

          <div className="space-y-4 rounded-[14px] border border-white/10 bg-white/[0.02] p-4">
            <div>
              <h3 className="text-sm font-semibold text-white/90">Team & roadmap</h3>
              <p className="mt-1 text-xs text-white/45">
                Add named team members with titles and bios under Investor portal → Team roster.
                The business plan Team section is built from that roster. Use the fields below only
                for supplemental narrative or if you have not configured a roster yet.
              </p>
            </div>
            <FormField label="Supplemental team narrative (optional)">
              <textarea
                {...register("foundingTeam")}
                className="sh-select min-h-[100px]"
                placeholder="Optional context not covered in team roster entries…"
              />
            </FormField>
            <FormField label="Milestones & roadmap">
              <textarea
                {...register("milestonesAndRoadmap")}
                className="sh-select min-h-[100px]"
                placeholder="What you have shipped, what is in progress, and major goals for the next 12–24 months…"
              />
            </FormField>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Funding stage">
              <select {...register("fundingStage")} className="sh-select">
                <option value="">Select stage</option>
                {FUNDING_STAGES.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Stage label (if other)">
              <input {...register("fundingStageLabel")} className="sh-select" />
            </FormField>
          </div>
          <FormField label="Previous funding raised ($)">
            <input
              type="number"
              {...register("previousFundingRaised")}
              className="sh-select"
            />
          </FormField>
          <FormField label="Previous investors">
            <textarea {...register("previousInvestors")} className="sh-select min-h-[64px]" />
          </FormField>
          <FormField label="Use of funds">
            <textarea {...register("useOfFunds")} className="sh-select min-h-[80px]" />
          </FormField>
          <FormField label="Funding timeline">
            <input {...register("fundingTimeline")} className="sh-select" />
          </FormField>
          <FormField label="Investor pitch (optional one-liner)">
            <textarea
              {...register("investorPitch")}
              className="sh-select min-h-[64px]"
              placeholder="We help dental clinics predict cash runway with live operational data."
            />
          </FormField>
          <FormField label="Generation guidance for AI">
            <textarea
              {...register("generationGuidance")}
              className="sh-select min-h-[120px]"
              placeholder="Tone, audience, emphasis (e.g. focus on B2B SaaS metrics, highlight enterprise pipeline, de-emphasize market size, stress path to profitability by month 18…)"
            />
          </FormField>
          <p className="text-xs text-white/45">
            Global guidance applies to every section. For section-specific drafts and polish
            instructions, use Section drafts & polish under Investor portal.
          </p>
          <div className="flex justify-end border-t border-white/10 pt-4">
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={saveMutation.isPending || !isDirty}
            >
              {saveMutation.isPending ? "Saving..." : "Save plan inputs"}
            </Button>
          </div>
        </form>
      )}
    </SettingsSectionCard>
  );
};
